import _groupBy from 'lodash/groupBy';
import moment from 'moment';
import { useMemo } from 'react';
import { v4 as uuid } from 'uuid';

import { flowNodeColor } from 'fable/flowEditor';
import { hexStringToNumber } from 'fable/flowEditor/components/flowManager/canvas/renderers/common/colors';
import { PhoneExtraData } from 'fable/flowEditor/components/flowManager/canvas/types/extraData';
import type {
  GenericNodeAttributes,
  GenericNodeProps,
} from 'fable/flowEditor/components/flowManager/canvas/types/node';
import { Text } from 'flow-builder/node/text';
import { TextBreak } from 'flow-builder/node/textBreak';
import { TextContent } from 'flow-builder/node/textContent';
import type { Relation } from 'flow-builder/types/relation';

import { createOpeningHoursModalContainer } from '../../../containers/modals/openingHours';
import { convertToHumanRelativeTime } from '../../../helpers/timeHelpers';
import { Day, FlowNodeTypes } from '../../types';

export type GroupedDaysT = Record<string, OpeningHoursNodeScheduleT[]>;
export type OpeningHours = { name: string; hours: string };

export type OpeningHoursNodeIntervalsT = {
  open: string;
  close: string;
};

export type OpeningHoursNodeScheduleT = {
  date: string;
  intervals: OpeningHoursNodeIntervalsT[];
};

export type OpeningHoursNode = Relation<
  OpeningHoursPayload,
  FlowNodeTypes.OPENING_HOURS
>;

export type OpeningHoursPayload = {
  openNodeId: string;
  closedNodeId: string;
  schedule: OpeningHoursNodeScheduleT[];
  timezone: string | null;
};

type OpeningHoursNodeAttributes = GenericNodeAttributes<
  OpeningHoursPayload,
  PhoneExtraData
>;
type OpeningHoursNodeProps = GenericNodeProps<
  OpeningHoursPayload,
  PhoneExtraData
>;

const translatePayload: OpeningHoursNodeAttributes['translatePayload'] = (
  node,
) => {
  const { closedNodeId, openNodeId } = node.payload;

  return [
    {
      id: uuid(),
      connection: closedNodeId || uuid(),
      label: 'Closed',
    },
    {
      id: uuid(),
      connection: openNodeId || uuid(),
      label: 'Open',
    },
  ];
};

const updateConnection: OpeningHoursNodeAttributes['updateConnection'] = (
  node,
  connection,
  connectionId,
) => {
  const entryName =
    connection.connectionLabel === 'Open' ? 'openNodeId' : 'closedNodeId';
  const payload: OpeningHoursPayload = {
    ...node.payload,
    [entryName]: connectionId,
  };

  return { ...node, payload };
};

export const createOpeningHoursNodeAttributes = (
  isDeprecated: boolean,
): OpeningHoursNodeAttributes => ({
  iconName: 'clock',
  label: 'Opening hours',
  color: hexStringToNumber(flowNodeColor.purple),
  attributes: {
    label: 'Opening...',
    isDeprecated,
  },
  helpText:
    'By setting your opening hours, you can decide what happens to incoming conversations based on whether your business is open or closed.',
  translatePayload,
  updateConnection,
  NodeContent: OpeningHoursNode,
  ActionModal: createOpeningHoursModalContainer(isDeprecated),
});

export const OpeningHoursNodeAttributes =
  createOpeningHoursNodeAttributes(false);
export const OpeningHoursDeprecatedNodeAttributes =
  createOpeningHoursNodeAttributes(true);

const weekdays: readonly Day[] = moment
  .weekdays()
  .map((weekday) => Day[weekday]);

const getOpeningHours = (intervals: OpeningHoursNodeIntervalsT[]): string => {
  const [lastInterval] = intervals;

  const open = convertToHumanRelativeTime(lastInterval?.open || '');
  const closed = convertToHumanRelativeTime(lastInterval?.close || '');

  return lastInterval ? `${open} - ${closed}` : 'Closed';
};

export const getGroupedDays = (
  schedule: OpeningHoursNodeScheduleT[],
): GroupedDaysT => {
  let index = 0;
  let currentKey = '';

  return _groupBy(schedule, (day: OpeningHoursNodeScheduleT): string => {
    const intervalStr: string = getOpeningHours(day.intervals);
    const keyStr = `${index}-[${intervalStr}]`;
    if (keyStr !== currentKey) {
      index += 1;
      currentKey = `${index}-[${intervalStr}]`;
    }
    return currentKey;
  });
};

const useOpeningHours = (schedule: OpeningHoursNodeScheduleT[]) => {
  return useMemo(() => {
    const groupedDays = getGroupedDays(schedule);
    return Object.values(groupedDays).map<OpeningHours>((days) => {
      const [firstDay] = days;
      const dayNames = days.map(({ date }) =>
        moment.utc(date, moment.ISO_8601).format('ddd'),
      );

      const isOnlyWeekdays =
        dayNames.length === 5 &&
        weekdays.slice(1, -1).every((day, index) => day === dayNames[index]);
      const isOnlyWeekend =
        dayNames.length === 2 &&
        [Day.Saturday, Day.Sunday].every(
          (day, index) => day === dayNames[index],
        );
      const day = {} as OpeningHours;

      if (isOnlyWeekdays) {
        day.name = 'Weekdays';
      }

      if (isOnlyWeekend) {
        day.name = 'Weekends';
      }

      if (!day.name) {
        day.name = dayNames.join(', ');
      }

      day.hours = getOpeningHours(firstDay.intervals);

      return day;
    });
  }, [schedule]);
};

export function OpeningHoursNode({ node }: OpeningHoursNodeProps): JSX.Element {
  const { payload } = node;

  const schedule = useOpeningHours(payload.schedule);

  return (
    <TextContent>
      {useMemo(
        () =>
          schedule.map(({ name, hours }) => (
            <TextBreak key={name}>
              <Text weight="normal" text={name} />
              <Text weight="semibold" text={hours} />
            </TextBreak>
          )),
        [schedule],
      )}
    </TextContent>
  );
}
